import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useMount } from 'ahooks';
import { unstable_batchedUpdates } from 'react-dom';
import { Dropdown, Radio, Input, Menu, Button, Spin, message, Select, Space } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { HTMLAttributes, memo, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { importAdminStores, ImportAdminStoresBody } from 'services/api/admin';
import insertAtCursor from 'utils/insertAtCursor';
import toLowerCase from 'utils/toLowerCase';
import useCheck from './useCheck';
import { useConfigList } from './useConfigList';
import { platformLabelMap } from '../../services/config/platform';
import { Store } from './share';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export interface BatchImportProps extends HTMLAttributes<HTMLDivElement> {
  departmentId?: number;
  permId?: General.PermissionId;
  supportWa?: boolean;
  storeDataSource?: Store[];
  singleRegion?: boolean | string;
  singlePlatform?: boolean | string;
}

function BatchImport(props: BatchImportProps) {
  const {
    departmentId,
    permId,
    supportWa = true,
    storeDataSource,
    singleRegion,
    singlePlatform,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const [importing, setImporting] = useState(false);

  const [type, setType] = useState<'storeName' | 'alias' | 'externalId'>('storeName');

  const { configList, fetch, loading } = useConfigList({
    supportWa,
    departmentId,
    storeDataSource,
    permId,
  });

  const { value: storeScopes, isChecked, onStoreChecked } = useCheck();

  const [
    {
      platform: firstSelectedPlatform = undefined,
      regionStoreScopes: [{ region: firstSelectedRegion = undefined } = {}] = [],
    } = {},
  ] = storeScopes;

  const [platformSelection, setPlatformSelection] = useState<
    General.PlatformUpperCaseTunnel | 'all'
  >('all');
  const [regionSelection, setRegionSelection] = useState<General.UpperCaseRegion | 'all'>('all');

  const typeLabelMap = {
    alias: t('ViewShops.店铺别名'),
    storeName: t('ViewShops.店铺名称'),
    externalId: t('store_list_label_store_platform_id'),
  };

  // 平台选择器选项
  const platformSelectOptions = useMemo(() => {
    const list = configList.map(({ platform }) => ({
      label: platformLabelMap[toLowerCase(platform)],
      value: platform,
    }));
    return [
      {
        label: t('feedback.全部'),
        value: 'all',
      },
      ...list,
    ];
  }, [configList, t]);

  // 站点选择器选项
  const regionSelectOptions = useMemo(() => {
    const defaultValue = [
      {
        label: t('feedback.全部'),
        value: 'all',
      },
    ];
    const configFound = configList.find((config) => config.platform === platformSelection);
    if (configFound) {
      const { regionList } = configFound;
      const list = regionList.map((region) => ({
        label: getRegionLabel(region.region),
        value: region.region,
      }));
      return defaultValue.concat(list);
    }

    const allRegions = new Set<General.UpperCaseRegion>();
    configList.forEach((configs) => {
      configs.regionList.forEach((region) => {
        allRegions.add(region.region);
      });
    });
    const list = Array.from(allRegions).map((region) => ({
      label: getRegionLabel(region),
      value: region,
    }));
    return defaultValue.concat(list);
  }, [configList, t, platformSelection, getRegionLabel]);

  const typeSelect = (
    <Space size={16}>
      <Select
        className={selectCls}
        options={platformSelectOptions}
        value={platformSelection}
        onChange={(value) => {
          unstable_batchedUpdates(() => {
            setPlatformSelection(value);
            setRegionSelection('all');
          });
        }}
      />
      <Select
        className={selectCls}
        options={regionSelectOptions}
        value={regionSelection}
        onChange={setRegionSelection}
      />
      <Radio.Group value={type} onChange={({ target: { value } }) => setType(value)}>
        {Object.keys(typeLabelMap).map((type) => {
          return (
            <Radio.Button key={type} value={type}>
              {typeLabelMap[type]}
            </Radio.Button>
          );
        })}
      </Radio.Group>
    </Space>
  );

  const [value, setValue] = useState('');

  const textareaRef = useRef<TextAreaRef>(null);

  const onPaste = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const { current: textarea } = textareaRef;

        if (textarea && text) {
          const { resizableTextArea } = textarea;

          if (resizableTextArea) {
            const { textArea } = resizableTextArea;

            const nextValue = insertAtCursor(textArea, text);

            setValue(nextValue);
          }
        }
      })
      .catch(() => {});
  };

  const input = (
    <Dropdown
      trigger={['contextMenu']}
      overlay={
        <Menu>
          <Menu.Item onClick={onPaste}>{t('ViewsChat.粘贴')}</Menu.Item>
        </Menu>
      }
    >
      <Input.TextArea
        ref={textareaRef}
        placeholder={t(`department.输入x可快速选择，多个店铺以回车分隔`, { x: typeLabelMap[type] })}
        value={value}
        onChange={({ target: { value } }) => setValue(value)}
      />
    </Dropdown>
  );

  const onImport = () => {
    setImporting(true);

    // 查询关键词不用去空格，因为店铺名称可能后面有空格 https://www.teambition.com/task/63722ed64f772200403dcf7f
    const keywords = value
      .split('\n')
      .filter((_) => _.trim())
      .map((_) => _.replaceAll('\u00A0', ' '));

    const platform = platformSelection === 'all' ? undefined : platformSelection;
    const region = regionSelection === 'all' ? undefined : regionSelection;

    const importStores = () => {
      if (!storeDataSource) {
        const body: ImportAdminStoresBody = {
          storeName: {
            type: 'storeName',
            storeName: keywords,
            departmentId,
            permId,
            platform,
            region,
          } as const,
          alias: {
            type: 'alias',
            alias: keywords,
            departmentId,
            permId,
            platform,
            region,
          } as const,
          externalId: {
            type: 'externalId',
            externalId: keywords,
            departmentId,
            permId,
            platform,
            region,
          } as const,
        }[type];

        return importAdminStores(body);
      }

      const filteredStoreList = storeDataSource.filter((store) => {
        return (
          (platform === undefined || platform === store.externalType) &&
          (region === undefined || region === store.region) &&
          ((type === 'alias' && keywords.includes(store.alias.replaceAll('\u00A0', ' '))) ||
            (type === 'storeName' &&
              keywords.includes(store.storeName.replaceAll('\u00A0', ' '))) ||
            (type === 'externalId' && keywords.includes(store.externalId)))
        );
      });

      return Promise.resolve(filteredStoreList);
    };

    importStores()
      .then((stores) => {
        if (!singleRegion && !singlePlatform) {
          return stores;
        }

        let _firstSelectedPlatform = firstSelectedPlatform;

        if (singlePlatform) {
          return stores.filter((store) => {
            if (_firstSelectedPlatform) {
              return store.externalType === _firstSelectedPlatform;
            }

            _firstSelectedPlatform = store.externalType;

            return true;
          });
        }

        let _firstSelectedRegion = firstSelectedRegion;

        return stores.filter((store) => {
          if (_firstSelectedPlatform && _firstSelectedRegion) {
            return (
              store.externalType === _firstSelectedPlatform && store.region === _firstSelectedRegion
            );
          }

          _firstSelectedPlatform = store.externalType;
          _firstSelectedRegion = store.region;

          return true;
        });
      })
      .then((stores) => {
        const unfoundKeywords = keywords.filter((keyword) => {
          return stores.every((store) => {
            const { storeName, alias, externalId } = store;

            if (type === 'alias') {
              // 可能出现关键词没空格但查询结果有空格的情况 https://www.teambition.com/task/63722ed64f772200403dcf7f
              return keyword.trim() !== alias.trim().replaceAll('\u00A0', ' ');
            }

            if (type === 'storeName') {
              // 可能出现关键词没空格但查询结果有空格的情况 https://www.teambition.com/task/63722ed64f772200403dcf7f
              return keyword.trim() !== storeName.trim().replaceAll('\u00A0', ' ');
            }

            return keyword.trim() !== externalId;
          });
        });

        if (unfoundKeywords.length === 0) {
          message.success(t('department.已导入'));

          setValue('');
        } else {
          message.warn(t(`department.x家店铺导入失败`, { x: unfoundKeywords.length }));

          setValue(unfoundKeywords.join('\n'));
        }

        const unselectedStores = stores.filter(({ id, externalType, region }) => {
          return !isChecked({
            idList: [id],
            platform: externalType,
            region,
          });
        });

        if (unselectedStores.length > 0) {
          onStoreChecked(true, unselectedStores);
        }
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      })
      .finally(() => {
        setImporting(false);
      });
  };

  useMount(() => {
    fetch().catch(() => {
      message.error(t('marketing.获取店铺列表失败，请稍后再试'));
    });
  });

  const button = (
    <Button type="primary" disabled={!value || !value.trim()} onClick={onImport}>
      {t('department.开始导入')}
    </Button>
  );

  return (
    <Container {...otherProps}>
      <Spin spinning={loading || importing}>
        {typeSelect}
        {input}
        {button}
      </Spin>
    </Container>
  );
}

export default memo((props: BatchImportProps) => {
  return <BatchImport key={props.departmentId} {...props} />;
});

const Container = styled.div`
  height: 100%;
  .ant-spin-nested-loading {
    height: 100%;
  }
  .ant-spin-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 22px 24px;
    > * + * {
      margin-top: 12px;
    }
    > textarea {
      flex: 1 0 0;
      line-height: 28px;
      resize: none;
    }
  }
`;

const selectCls = css`
  min-width: 114px;
`;
